import React, {useEffect, useState} from "react";
import BookingFooterNew from "pages/Front/Booking/BookingFooterNew";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import clsx from "clsx";
import BookingLoginReservationDetailInput from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationDetailInput";
import BookingLoginReservationButton from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import BookingLoginReservationButtonInner from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButtonInner";
import {BookingLoginReservationButtonSize} from "types/enums/BookingLoginReservationButtonSize";
import {BookingLoginReservationButtonStyle} from "types/enums/BookingLoginReservationButtonStyle";
import {PaperAirplaneIcon} from "@heroicons/react/24/solid";
import {REGEX_EMAIL} from "utils/utility";
import BlacklistAction, {SendBlacklistPostalTypeEnum} from "store/blacklist/BlacklistAction";
import {toast} from "react-toastify";
import LoadingIcon from "components/Icons/LoadingIcon";
import BookingLoginZuumeHeader from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";

interface BlacklistPostalProps {}

const BlacklistPostal = (props: BlacklistPostalProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const classNameActiveButton =
        "flex bg-black rounded-tl-md rounded-tr-md justify-center items-center px-4 py-3 text-white border border-b-0 border-black w-full font-bold text-center";
    const classNameInactiveButton =
        "flex bg-gray-50 rounded-tl-md rounded-tr-md justify-center items-center px-4 py-3 text-gray-500 border border-b-0 border-gray-200 hover:text-black hover:border-black w-full font-bold text-center";
    const [inputForView, setInputForView] = useState<SendBlacklistPostalTypeEnum>(SendBlacklistPostalTypeEnum.Email);
    const isEmailView: boolean = inputForView === SendBlacklistPostalTypeEnum.Email;
    const BlacklistPostalEmailFormSchema = yup.object().shape({
        email: yup
            .string()
            .required(t("Email is required field."))
            .test("email", t("Invalid email format"), value => {
                return REGEX_EMAIL.test(value);
            }),
    });
    const BlacklistPostalIdFormSchema = yup.object().shape({
        id: yup.string().required(t("ID name must be filled")),
    });
    type EmailInputs = {email: string; id?: never};
    type IdInputs = {id: string; email?: never};
    type Inputs = EmailInputs | IdInputs;
    const {
        register,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<Inputs>({
        resolver: yupResolver(isEmailView ? BlacklistPostalEmailFormSchema : BlacklistPostalIdFormSchema),
    });

    const onErrorHandler = (dataErrors: any) => {
        console.log("onErrorHandler", dataErrors);
    };

    const onSubmitHandler = (data: Inputs) => {
        if (isLoading) {
            return true;
        }
        setIsLoading(true);

        const identifier = data?.email ?? data?.id;
        const type = isEmailView ? SendBlacklistPostalTypeEnum.Email : SendBlacklistPostalTypeEnum.Id;

        if (!identifier) {
            toast.error(t("Please enter a valid email or ID."));
            setIsLoading(false);
            return Promise.resolve();
        }

        dispatch(BlacklistAction.fetchSendBlacklistPostal(identifier, type))
            .then(r => {
                toast.success(t("Record sent to the blacklist postal service."));
                reset();
            })
            .catch(err => {
                toast.error(t("Error."));
                console.error(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
        return Promise.resolve();
    };

    useEffect(() => {
        reset();
    }, [inputForView]);

    return (
        <div className={"BlacklistPostal h-full flex flex-col flex-grow m-auto justify-between w-full"}>
            <div className={"BookingUpsells mx-auto w-full max-w-3xl relative space-y-8"}>
                <div className={"text-center"}>
                    <BookingLoginZuumeHeader title={t("Blacklist postal")} />
                </div>

                {/*<BookingCard className={clsx("BlacklistPostalCard !px-0 !py-0 mb-6")}>*/}
                {/*    <form autoComplete={"off"} className={"grid grid-cols-1 px-3 py-3"} onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>*/}
                <form autoComplete={"off"} className={"grid grid-cols-1"} onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                    <div className={"flex flex-row justify-between gap-x-2"}>
                        <button
                            className={clsx(isEmailView ? classNameActiveButton : classNameInactiveButton)}
                            type={"button"}
                            onClick={() => setInputForView(SendBlacklistPostalTypeEnum.Email)}
                        >
                            {t("Email")}
                        </button>
                        <button
                            className={clsx(isEmailView ? classNameInactiveButton : classNameActiveButton)}
                            type={"button"}
                            onClick={() => setInputForView(SendBlacklistPostalTypeEnum.Id)}
                        >
                            {t("ID")}
                        </button>
                    </div>
                    <div className={"border border-black px-3 py-3 mb-3 rounded-bl-md rounded-br-md bg-white"}>
                        {isEmailView ? (
                            <BookingLoginReservationDetailInput className={"col-span-1"} errors={errors} name={"email"} register={register} />
                        ) : (
                            <BookingLoginReservationDetailInput className={"col-span-1"} errors={errors} name={"id"} register={register} />
                        )}
                    </div>
                    <BookingLoginReservationButton className={"flex w-auto min-w-[200px] mx-auto"} onClick={handleSubmit(onSubmitHandler, onErrorHandler)}>
                        <BookingLoginReservationButtonInner
                            className={"inline-flex w-full"}
                            size={BookingLoginReservationButtonSize.Normal}
                            style={BookingLoginReservationButtonStyle.Green}
                        >
                            {isLoading ? <LoadingIcon className={"w-4 h-4 flex-none"} /> : <PaperAirplaneIcon className={"w-4 h-4 flex-none"} />}
                            <span className={"flex ml-2"}>{t("Send")}</span>
                        </BookingLoginReservationButtonInner>
                    </BookingLoginReservationButton>
                </form>
                {/*</BookingCard>*/}

                <p className="text-center text-gray-600">
                    {t("Only email addresses or IDs can be added to the blacklist postal service. Please enter a valid email address or ID above.")}
                </p>
            </div>
            <BookingFooterNew />
        </div>
    );
};

export default BlacklistPostal;
